import localforage from "localforage";
import { compressorAPI, paymentAPI } from "..";
import axios from "axios";
import React from "react";
import {
  publishLocalUploadProgress,
  publishUploadQueued,
  UPLOAD_BACKLOG_NOTICE,
} from "../components/UploadToast/UploadToast";
import { decodeJWTPayload } from "./jwt";

export { decodeJWTPayload };

export type MediaType = "image" | "video" | "audio";

export type MediaSection = "hallAd" | "radioAd" | "tvAd" | "orgAvatar" | "collaboratorAvatar";

export interface UploadIdentifiers {
  hallAdId?: string;
  radioAdId?: string;
  tvAdId?: string;
  orgId?: string;
  collaboratorId?: string;
}

export interface UploadProgress {
  currentChunk: number;
  totalChunks: number;
  percent: number;
  status: number;
}

export interface UploadBaseResult {
  ok: boolean;
  status: number;
  error?: string;
  cancelled?: boolean;
  chunk?: number;
  responseBody?: any;
  // The rare per-user backlog-cap soft reject (HTTP 429 + body
  // `{error:"backlog_full"}`). It is NOT a real upload failure: the user simply
  // has too many uploads queued at once. Callers must surface it as a friendly
  // "na fila / aguarde" notice, never as an error.
  backlog?: boolean;
}

export interface UploadMediaOptions {
  ticketId: string;
  file: File;
  mediaId: string;
  mediaType: MediaType;
  section: MediaSection;
  identifiers?: UploadIdentifiers;
  chunkSizeMB?: number;
  maxRetries?: number;
  getToken: () => Promise<string>;
  onProgress?: (progress: UploadProgress) => void;
}

export interface TurboUploadOptions extends UploadMediaOptions {
  parallel?: number;
  // Abort the transfer if NO chunk completes within this inactivity window
  // (tus-style stall timeout). Defaults to 90s. This never bounds total
  // wall-clock, only inactivity, so a slow but alive heavy upload keeps going.
  stallTimeoutMs?: number;
}

// Client-side upload ceiling for heavy media. This mirrors the app FE constant
// and is only a picker-side guard: the compressor is the source of truth via its
// own cap (COMPRESSOR_MAX_FILE_MB, default 500MiB in
// backend/dakasa-media-compressor/controllers/limits.go). The two are COUPLED and
// must be kept in step. The client cap must NEVER exceed the deployed server cap,
// or the user picks a file the server then rejects at merge time with a
// "too_large" terminal.
//
// TODO(upload-cap): raise COMPRESSOR_MAX_FILE_MB on the compressor deployment to
// 10240 so this 10GB ceiling is accepted end to end. Until that server env is
// raised, files above the server cap still fail server-side, so keep this value at
// or below the deployed COMPRESSOR_MAX_FILE_MB.
export const MAX_UPLOAD_FILE_BYTES = 10 * 1024 * 1024 * 1024; // 10 GiB heavy-media ceiling

export type TurboUploadResult = UploadBaseResult;

export interface TurboUploadController {
  cancel: () => void;
  isCancelled: () => boolean;
}

export interface TurboUploadReturn {
  controller: TurboUploadController;
  promise: Promise<TurboUploadResult>;
}

export function createTurboUpload(options: TurboUploadOptions): TurboUploadReturn {
  const { file, parallel = 4, chunkSizeMB = 4 } = options;

  const abortController = new AbortController();
  let cancelled = false;

  const controller: TurboUploadController = {
    cancel: () => {
      cancelled = true;
      abortController.abort();
    },
    isCancelled: () => cancelled,
  };

  const promise = new Promise<TurboUploadResult>(async (resolve) => {
    const {
      ticketId,
      mediaId,
      mediaType,
      section,
      identifiers = {},
      maxRetries = 5,
      stallTimeoutMs,
      getToken,
      onProgress = () => {},
    } = options;

    // Client-side heavy-media guard. See MAX_UPLOAD_FILE_BYTES for the coupling
    // note: the compressor cap (COMPRESSOR_MAX_FILE_MB) is the source of truth.
    if (file.size > MAX_UPLOAD_FILE_BYTES) {
      return resolve({
        ok: false,
        status: -1,
        error: "Arquivo maior que 10GB",
        cancelled: false,
        chunk: 0,
      });
    }

    const ready = await waitTicketReady(ticketId);
    if (!ready) {
      return resolve({
        ok: false,
        status: 500,
        error: "ticket not ready in time",
        cancelled: false,
        chunk: 0,
      });
    }

    const chunkSize = chunkSizeMB * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSize);

    // Server-assisted resume (H15): ask the compressor which chunks it already
    // stored and skip re-sending them. Always (re)send the last chunk even if it
    // is present, so the finalize/enqueue trigger fires (the write is idempotent).
    // Fail-open on any status error: send the full range, exactly as before.
    const alreadyPresent = await fetchPresentChunks(totalChunks);
    const chunks = Array.from({ length: totalChunks }, (_, i) => i + 1).filter(
      (n) => n === totalChunks || !alreadyPresent.has(n),
    );

    // Persist enough to resume across a reload: mediaId, ticketId, totalChunks.
    await persistResume();

    // Baseline progress at the count we skip so a resumed upload does not restart
    // the toast at 0%.
    let completedChunks = totalChunks - chunks.length;
    if (completedChunks > 0) {
      publishLocalUploadProgress(
        mediaId,
        Math.floor((completedChunks / totalChunks) * 100),
        section,
      );
    }

    // Per-chunk stall watchdog (H14): abort only if NO chunk completes within the
    // inactivity window, re-armed on every ack. Total wall-clock is irrelevant, so
    // a slow but alive multi-GB upload keeps going; a true stall still fails fast.
    const stallWindow = stallTimeoutMs ?? 90 * 1000;
    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    const clearStall = () => {
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };
    const armStall = () => {
      clearStall();
      stallTimer = setTimeout(() => {
        cancelled = true;
        abortController.abort();
        resolve({
          ok: false,
          status: -1,
          error: `Upload travou: nenhum chunk concluído em ${Math.round(stallWindow / 1000)}s`,
          cancelled: true,
        });
      }, stallWindow);
    };

    async function fetchPresentChunks(totalChunks: number): Promise<Set<number>> {
      try {
        const token = await getToken();
        const res = await compressorAPI.get("/chunk-status", {
          params: { mediaId, totalChunks },
          headers: { "X-JWT": token },
          signal: abortController.signal,
        });
        const received = res?.data?.received;
        if (Array.isArray(received)) {
          return new Set<number>(
            received.filter((n: unknown): n is number => typeof n === "number"),
          );
        }
      } catch {
        // Fail-open: if the status endpoint is unavailable, upload the full range.
      }
      return new Set<number>();
    }

    async function persistResume(): Promise<void> {
      try {
        await localforage.setItem(`upload:resume:${mediaId}`, {
          mediaId,
          mediaType,
          ticketId,
          totalChunks,
          section,
          updatedAt: Date.now(),
        });
      } catch {
        // Best-effort: resume still works within a session via chunk-status.
      }
    }

    async function clearResume(): Promise<void> {
      try {
        await localforage.removeItem(`upload:resume:${mediaId}`);
      } catch {
        // Ignore: a stale resume record is harmless (chunk-status is idempotent).
      }
    }

    async function waitTicketReady(ticketId: string, maxWait = 5000) {
      const start = Date.now();

      while (Date.now() - start < maxWait) {
        try {
          const token = await getToken();

          const res = await compressorAPI.post(
            `/check-ticket`,
            { ticketId: ticketId },
            {
              headers: { "X-JWT": token },
              signal: abortController.signal,
            },
          );

          if (res.status === 200) return true;
        } catch {}

        await sleep(150);
      }

      return false;
    }

    async function uploadChunk(chunkIndex: number): Promise<boolean> {
      const start = (chunkIndex - 1) * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunkBlob = file.slice(start, end);

      const form = new FormData();
      form.append("chunkContent", chunkBlob);
      form.append("currentChunk", String(chunkIndex));
      form.append("totalChunks", String(totalChunks));
      form.append("mediaId", mediaId);
      form.append("mediaType", mediaType);
      form.append("section", section);
      form.append("ticketId", ticketId);

      Object.entries(identifiers).forEach(([k, v]) => {
        if (v) form.append(k, v);
      });

      let attempt = 0;
      // Snapshot the count of chunks already finished BEFORE this iteration
      // starts. The progress callback below closes over it so reads are
      // stable even though `completedChunks` is mutated after each
      // chunk — also satisfies eslint `no-loop-func` without a disable.
      const chunksDoneBeforeThis = completedChunks;

      while (attempt <= maxRetries) {
        if (cancelled) return false;

        try {
          const token = await getToken();

          const res = await compressorAPI.post("/compress", form, {
            headers: { "X-JWT": token },
            signal: abortController.signal,
            // Byte-level upload progress per chunk — combined with the count of
            // chunks already done, gives the toast continuous progress instead
            // of jumping in 1/N steps.
            onUploadProgress: (e) => {
              if (!e.total) return;
              const fraction = e.loaded / e.total; // 0..1 within this chunk
              const overall = Math.min(
                99,
                Math.floor(((chunksDoneBeforeThis + fraction) / totalChunks) * 100),
              );
              publishLocalUploadProgress(mediaId, overall, section);
            },
          });

          // A finalize claim is not a durable queue acknowledgement. Retrying
          // the same chunk is idempotent; retain the resume record if the
          // compressor cannot confirm acceptance within this retry budget.
          if (res.status === 202 && res.data?.queued === false) {
            armStall();
            if (attempt >= maxRetries) {
              return fail(chunkIndex, 503, "upload_finalizing", { code: "upload_finalizing" });
            }
            attempt++;
            await sleep(backoff(attempt));
            continue;
          }

          // A chunk landed: reset the stall watchdog.
          armStall();

          completedChunks++;
          const percent = Math.floor((completedChunks / totalChunks) * 100);
          // Surface chunk progress through the UploadToast pipeline. The compressor
          // only starts emitting RTA progress events after the last chunk lands and
          // ProcessMedia kicks in, so without this the toast would sit at 0% during
          // the entire client-side upload phase.
          publishLocalUploadProgress(mediaId, percent, section);
          // 202 = the durable-queue compressor accepted the finished upload into
          // its queue. Show "na fila" until a worker starts emitting progress.
          if (res.status === 202) {
            publishUploadQueued(mediaId, section);
          }
          onProgress({
            currentChunk: chunkIndex,
            totalChunks,
            percent,
            status: res.status,
          });

          return true;
        } catch (err: any) {
          if (cancelled || axios.isCancel(err)) {
            return fail(chunkIndex, -1, "Upload cancelado");
          }

          const status = err?.response?.status ?? -1;
          const body = err?.response?.data;

          // Backlog-cap soft reject: the per-user queue is full (anti-abuse). This
          // is not a failure, it is "na fila, aguarde". Show the friendly notice on
          // the toast (keyed by mediaId) and resolve with `backlog` so the caller
          // routes it as a notice, not a scary error. `too_many_uploads` is the
          // legacy code the twin emitted before the durable queue; treat both the
          // same so this stays correct across the rollout.
          if (
            status === 429 &&
            (body?.error === "backlog_full" || body?.error === "too_many_uploads")
          ) {
            const notice = UPLOAD_BACKLOG_NOTICE;
            publishUploadQueued(mediaId, section, notice);
            cancelled = true;
            abortController.abort();
            resolve({
              ok: false,
              status,
              error: "backlog_full",
              cancelled: true,
              chunk: chunkIndex,
              responseBody: body,
              backlog: true,
            });
            return false;
          }

          if (status >= 400 && status < 500 && status !== 401) {
            return fail(chunkIndex, status, "Erro de requisição", body);
          }

          if (status === 401) {
            attempt++;
            if (attempt >= maxRetries) {
              return fail(chunkIndex, status, "401 persistente", body);
            }
            await sleep(backoff(attempt));
            continue;
          }

          // Other errors (5xx, network): retry THIS chunk with jittered backoff,
          // bounded only by its own attempt budget. The shared cross-chunk breaker
          // (persistentErrors >= 3) is gone on purpose so a few clustered transient
          // blips across thousands of chunks do not discard a multi-GB upload. A
          // genuinely stuck chunk still fails at the post-loop guard below, and a
          // dead server is caught by the stall watchdog.
          attempt++;
          await sleep(backoff(attempt));
        }
      }

      // Retry budget exhausted without a single ack: fail loudly. A bare
      // `return false` here would leave `cancelled` false and let the pool join
      // resolve ok:true on a chunk that never transferred (mirror of the app
      // pipeline's post-loop guard).
      return fail(chunkIndex, -1, "Falha no upload: tentativas esgotadas");
    }

    function fail(chunk: number, status: number, error: string, responseBody?: any): false {
      cancelled = true;
      abortController.abort();
      resolve({
        ok: false,
        status,
        error,
        cancelled: true,
        chunk,
        responseBody,
      });
      return false;
    }

    async function worker() {
      while (chunks.length > 0 && !cancelled) {
        const index = chunks.shift();
        if (!index) return;
        const ok = await uploadChunk(index);
        if (!ok) return;
      }
    }

    // Arm the stall watchdog for the transfer, then run the worker pool.
    armStall();
    await Promise.all(Array.from({ length: parallel }, worker));
    clearStall();

    if (!cancelled) {
      // Every chunk is uploaded and the finalize is enqueued: the resume record
      // is no longer needed (a reload would re-query chunk-status and find the set
      // complete anyway).
      await clearResume();
      resolve({ ok: true, status: 200 });
    }
  });

  return { controller, promise };
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function backoff(attempt: number) {
  const ceiling = Math.min(2000 * Math.pow(1.5, attempt), 15000);
  // Full jitter: spread retries across [ceiling/2, ceiling] so that when a blip
  // hits many chunks at once they do not all resync into a thundering herd. This
  // is what lets the uploader lean on the per-chunk attempt budget instead of a
  // shared cross-chunk error breaker (see uploadChunk).
  return Math.round(ceiling / 2 + Math.random() * (ceiling / 2));
}

export class validateField {
  static email(email: string): Error | void {
    const r =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    if (!r.test(email)) {
      return new Error(`O email ${email} não é válido`);
    }
  }

  static fullName(name: string): Error | void {
    const r = /^[A-Za-zÀ-ÿ]+(?:\s[A-Za-zÀ-ÿ]+)*$/;

    if (!r.test(name.normalize("NFC"))) {
      return new Error(`Não pode usar caracteres especiais para nomes`);
    }
  }

  static password(password: string): Error | void {
    let hasUpper: boolean = false;
    let hasLower: boolean = false;
    let hasNum: boolean = false;
    let hasSpecialChar: boolean = false;

    if (password.length < 8) {
      return new Error(`A senha precisa ser mais comprida`);
    }

    for (let i = 0; i < password.length; i++) {
      const char = password[i];

      if ("0123456789".includes(char)) {
        hasNum = true;
        continue;
      }

      if (/[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(char)) {
        hasSpecialChar = true;
        continue;
      }

      if (char === password[i].toUpperCase()) {
        hasUpper = true;
        continue;
      }

      if (char === password[i].toLowerCase()) {
        hasLower = true;
      }
    }

    if (!hasUpper) {
      return new Error(`Senha precisa ter ao menos uma letras maiúsculas`);
    }

    if (!hasLower) {
      return new Error(`Senha precisa ter ao menos uma letra minúscula`);
    }

    if (!hasNum) {
      return new Error(`Senha precisa ter ao menos um número`);
    }

    if (!hasSpecialChar) {
      return new Error(`Senha precisa ter ao menos um caractere especial`);
    }
  }

  static birthday(birthday: string): Error | void {
    const age = new Date().getFullYear() - new Date(birthday).getFullYear();

    if (age > 313) {
      return new Error("Você não é tão velho assim");
    }

    if (age < 13) {
      return new Error("Menores de 13 anos não podem criar contas");
    }
  }

  static number(number: any): Error | void {
    const phone = String(number ?? "").trim();
    if (phone === "") {
      return new Error("Informe um telefone");
    }

    // Allow digits, optional leading +, spaces, dashes and parentheses.
    const r = /^\+?[0-9\s\-()]+$/;
    if (!r.test(phone)) {
      return new Error("Use apenas números, +, espaço, parênteses ou traços");
    }
  }

  static noSpecialChar(key: string, value: string): Error | void {
    const r = /^[A-Za-zÀ-ÿ0-9\s]+$/;

    if (!r.test(value)) {
      return new Error(`Não pode usar caracteres especiais no campo "${key}"`);
    }
  }
}

export class adsPrice {
  duration: number;
  size: number;
  JWT: string;
  feature: "hall" | "radio" | "tv";

  constructor(size: number, duration: number, feature: "hall" | "radio" | "tv", JWT: string) {
    this.JWT = JWT;
    this.duration = Math.round(duration);
    this.size = size;
    this.feature = feature;
  }

  async price(): Promise<{ view: number; access: number }> {
    const headers = {
      "X-JWT": this.JWT,
    };

    const body = {
      feature: this.feature,
      durationSec: this.duration,
      sizeMB: this.size / 1024 / 1024,
    };

    try {
      const res = await paymentAPI.post("/generate-item-amount", body, { headers: headers });
      const data = res.data;
      return {
        view: data.viewAmountInCents / 100,
        access: data.accessAmountInCents / 100,
      };
    } catch (error) {
      console.error("Failed to fetch ad price:", error);
      return {
        view: 0,
        access: 0,
      };
    }
  }
}

export function isReadonlyStringArray(arr: unknown): arr is readonly string[] {
  return Array.isArray(arr) && arr.every((item) => typeof item === "string");
}

export function onlyIntegers(str: string): number {
  return Number(str.replace(/\D/g, ""));
}

export function onlyNumbers(str: string): number {
  return Number(str.replace(/[^0-9.-]/g, ""));
}

export const sessionInfo = async (): Promise<any> => {
  const token = (await localforage.getItem("JWT")) as string | undefined;
  return decodeJWTPayload(token);
};

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function capitalize(str: string): string {
  if (str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function useViewportSize() {
  const [size, setSize] = React.useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  }));

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return size;
}

export function alertYesNo(
  message: string,
  type: "success" | "error" | "info" = "info",
  yesAction?: () => void,
) {
  const alertElementContainer = document.createElement("div");
  const alertElementContent = document.createElement("div");
  const textContainer = document.createElement("span");
  const buttonsContainer = document.createElement("div");
  const noButton = document.createElement("button");
  const yesButton = document.createElement("button");
  alertElementContainer.className = `customAlertContainer customAlert-${type}`;
  alertElementContent.className = "customAlertContent";
  buttonsContainer.className = "customAlertButtonsContainer";
  textContainer.textContent = message;

  noButton.textContent = "Cancelar";
  yesButton.textContent = "Confirmar";

  noButton.onclick = () => {
    alertElementContainer.style.opacity = "0";
    setTimeout(() => {
      document.body.removeChild(alertElementContainer);
    }, 300);
  };

  yesButton.onclick = () => {
    alertElementContainer.style.opacity = "0";
    setTimeout(() => {
      document.body.removeChild(alertElementContainer);
      if (yesAction) yesAction();
    }, 300);
  };

  buttonsContainer.appendChild(noButton);
  buttonsContainer.appendChild(yesButton);
  alertElementContent.appendChild(textContainer);
  alertElementContent.appendChild(buttonsContainer);
  alertElementContainer.appendChild(alertElementContent);

  document.body.appendChild(alertElementContainer);
  setTimeout(() => {
    alertElementContainer.style.opacity = "1";
  }, 10);
}

export function getBandwidthMbps(): number {
  if (typeof navigator === "undefined") return 0;
  const connection =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;
  if (!connection) return 0;
  if (typeof connection.downlink === "number" && connection.downlink > 0) {
    return connection.downlink;
  }
  const effective = connection.effectiveType as string | undefined;
  switch (effective) {
    case "slow-2g":
      return 0.05;
    case "2g":
      return 0.1;
    case "3g":
      return 1;
    case "4g":
      return 5;
    default:
      return 0;
  }
}
